import { supabase } from './supabaseClient';
import { migrateContentBase64, migrateMediaSrcToStorage } from './storageService';
import { trackEvent } from './analyticsService';

/**
 * Получить текущего пользователя (аналогично studentsService.js)
 */
async function getCurrentUser() {
  const currentUserStr = localStorage.getItem('currentUser');
  if (currentUserStr) {
    try {
      const user = JSON.parse(currentUserStr);
      if (user && user.id) {
        // Пробуем teachers (для преподавателей)
        const { data: teacherData, error: teacherError } = await supabase
          .from('teachers')
          .select('*')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        
        if (!teacherError && teacherData) {
          return {
            ...teacherData,
            id: teacherData.auth_user_id,
            role: 'teacher'
          };
        }
        
        // Если не преподаватель, возвращаем из localStorage
        return user;
      }
    } catch (e) {
      console.error('Error parsing currentUser from localStorage:', e);
    }
  }
  
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (authUser) {
    const { data: teacherData } = await supabase
      .from('teachers')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    
    if (teacherData) {
      return {
        ...teacherData,
        id: teacherData.auth_user_id,
        role: 'teacher'
      };
    }
  }
  
  return null;
}

/**
 * Конвертировать studentId (может быть slug или UUID) в UUID
 */
async function resolveStudentId(studentId) {
  if (!studentId) return null;
  
  // Проверяем, является ли studentId UUID
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
  
  if (isUUID) {
    return studentId;
  }
  
  // Если это slug, находим student.id по invite_slug
  const { data: studentData } = await supabase
    .from('students')
    .select('id')
    .eq('invite_slug', studentId)
    .maybeSingle();
  
  if (studentData) {
    return studentData.id;
  }
  
  console.warn('Student not found by slug:', studentId);
  return null;
}

/**
 * Конвертировать урок из localStorage формата в Supabase формат
 */
async function convertLessonToSupabaseFormat(lesson) {
  // Конвертируем studentId из slug в UUID, если нужно
  const studentId = await resolveStudentId(lesson.studentId);
  
  // Проверяем, является ли id валидным UUID
  const isValidUUID = lesson.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lesson.id);
  
  const result = {
    teacher_id: null, // Будет установлено при сохранении
    student_id: studentId,
    group_id: lesson.groupId || null,
    title: lesson.title || '',
    content: {
      bannerImage: lesson.bannerImage || null,
      docBanners: lesson.docBanners || {},
      homeworkBanners: lesson.homeworkBanners || {},
      containers: lesson.containers || [],
      docContainers: lesson.docContainers || [],
      homeworkContainers: lesson.homeworkContainers || [],
      // Per-homework metadata (title, description, dueDate, bannerImage)
      // that the teacher edited inside the lesson workspace BEFORE
      // clicking "Send Homework". Without persisting this the teacher's
      // pre-Send drafts get lost on reload.
      homeworkDataCache: lesson.homeworkDataCache || {},
      whiteboards: lesson.whiteboards || [],
      editorContents: lesson.editorContents || [],
      whiteboardContents: lesson.whiteboardContents || [],
      containerComments: lesson.containerComments || {},
      insertedRoundVideos: lesson.insertedRoundVideos || [],
      insertedRoundAudios: lesson.insertedRoundAudios || [],
      insertedRoundTexts: lesson.insertedRoundTexts || [],
    },
    date: lesson.date ? new Date(lesson.date).toISOString().split('T')[0] : null,
    time_from: lesson.timeFrom || lesson.from || null,
    time_to: lesson.timeTo || lesson.to || null,
    status: lesson.status || 'planned',
    type: lesson.type || 'lesson',
    created_at: lesson.createdAt || new Date().toISOString(),
    updated_at: lesson.updatedAt || new Date().toISOString()
  };
  
  // Включаем id только если это валидный UUID
  if (isValidUUID) {
    result.id = lesson.id;
  }
  
  return result;
}

/**
 * Конвертировать урок из Supabase формата в localStorage формат
 */
function convertLessonFromSupabaseFormat(lesson) {
  if (!lesson) return null;

  const content = lesson.content || {};
  
  return {
    id: lesson.id,
    studentId: lesson.student_id || null,
    groupId: lesson.group_id || null,
    title: lesson.title || '',
    bannerImage: content.bannerImage || null,
    docBanners: content.docBanners || {},
    homeworkBanners: content.homeworkBanners || {},
    containers: content.containers || [],
    docContainers: content.docContainers || [],
    homeworkContainers: content.homeworkContainers || [],
    homeworkDataCache: content.homeworkDataCache || {},
    whiteboards: content.whiteboards || [],
    editorContents: content.editorContents || [],
    whiteboardContents: content.whiteboardContents || [],
    containerComments: content.containerComments || {},
    insertedRoundVideos: content.insertedRoundVideos || [],
    insertedRoundAudios: content.insertedRoundAudios || [],
    insertedRoundTexts: content.insertedRoundTexts || [],
    date: lesson.date || new Date().toLocaleDateString(),
    timeFrom: lesson.time_from || null,
    timeTo: lesson.time_to || null,
    from: lesson.time_from || null,
    to: lesson.time_to || null,
    status: lesson.status || 'planned',
    type: lesson.type || 'lesson',
    createdAt: lesson.created_at || new Date().toISOString(),
    updatedAt: lesson.updated_at || new Date().toISOString()
  };
}

/**
 * Конвертировать запись списка урока (без content) из Supabase в формат приложения
 */
function convertLessonListFromSupabase(row) {
  if (!row) return null;
  return {
    id: row.id,
    studentId: row.student_id || null,
    groupId: row.group_id || null,
    title: row.title || '',
    bannerImage: null,
    docBanners: {},
    homeworkBanners: {},
    containers: [],
    docContainers: [],
    homeworkContainers: [],
    whiteboards: [],
    editorContents: [],
    whiteboardContents: [],
    containerComments: {},
    date: row.date || null,
    timeFrom: row.time_from || null,
    timeTo: row.time_to || null,
    from: row.time_from || null,
    to: row.time_to || null,
    status: row.status || 'planned',
    type: row.type || 'lesson',
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null
  };
}

/**
 * Fallback: загрузить уроки из localStorage
 *
 * Canonical read/write pair for the 'novas_lessons_data' cache — the single
 * source of truth for this key across the app (StudentProfile.js,
 * GroupProfile.jsx, Home.js, Business.js all route through these instead of
 * touching localStorage directly, so the cache can't drift out of sync with
 * itself the way the old 'novas-lessons'/'novas_lessons_data' dual-write
 * pattern could).
 */
export function getLessonsFromLocalStorage() {
  try {
    const stored = localStorage.getItem('novas_lessons_data');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading lessons from localStorage:', error);
    return [];
  }
}

export function saveLessonsToLocalStorage(lessons) {
  try {
    localStorage.setItem('novas_lessons_data', JSON.stringify(lessons));
  } catch (error) {
    console.error('Error saving lessons to localStorage:', error);
  }
}

/**
 * Получить все уроки текущего преподавателя
 */
export async function getLessons() {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated. Please log in to view lessons.');
    }

    // Получаем teacher.id по auth_user_id
    const teacherId = user.role === 'teacher' ? user.id : null;
    if (!teacherId) {
      throw new Error('User is not a teacher');
    }
    
    // Получаем teacher record
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacherId)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('teacher_id', teacher.id)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching lessons from Supabase:', error);
      throw error;
    }

    // Конвертируем из Supabase формата
    const convertedLessons = data.map(convertLessonFromSupabaseFormat);
    
    // Фильтруем: показываем только уроки с контентом (не из расписания)
    const lessonsWithContent = convertedLessons.filter(lesson => {
      // Проверяем, есть ли у урока контент (полноценный урок, а не просто запись из расписания)
      const hasContent = lesson.containers?.length > 0 || 
                        lesson.docContainers?.length > 0 ||
                        lesson.homeworkContainers?.length > 0 ||
                        lesson.whiteboards?.length > 0 ||
                        lesson.editorContents?.length > 0 ||
                        lesson.whiteboardContents?.length > 0 ||
                        lesson.bannerImage ||
                        (lesson.content && typeof lesson.content === 'object' && Object.keys(lesson.content).length > 0);
      
      return hasContent;
    });
    
    console.log('getLessons - Total lessons:', convertedLessons.length, 'Lessons with content:', lessonsWithContent.length);
    
    return lessonsWithContent;
  } catch (error) {
    console.error('Error in getLessons:', error);
    throw error;
  }
}

/**
 * Получить урок по ID
 * Работает как для преподавателей, так и для студентов
 */
export async function getLessonById(lessonId) {
  try {
    console.log('getLessonById called with lessonId:', lessonId);
    
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const allLessons = getLessonsFromLocalStorage();
      return allLessons.find(lesson => lesson.id === lessonId) || null;
    }

    // Если пользователь - преподаватель, проверяем teacher_id
    if (user.role === 'teacher') {
      const { data: teacher } = await supabase
        .from('teachers')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      
      if (!teacher) {
        console.warn('Teacher not found');
        const allLessons = getLessonsFromLocalStorage();
        return allLessons.find(lesson => lesson.id === lessonId) || null;
      }
      
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('id', lessonId)
        .eq('teacher_id', teacher.id)
        .single();

      if (error) {
        console.error('Error fetching lesson from Supabase (teacher):', error);
        throw error;
      }

      if (!data) {
        throw new Error(`Lesson not found: ${lessonId}`);
      }

      console.log('getLessonById - loaded lesson:', data.id);
      return convertLessonFromSupabaseFormat(data);
    }
    
    // Если пользователь - студент, загружаем урок без проверки teacher_id
    // RLS политика "Students can view own lessons" обеспечит доступ только к своим урокам.
    // maybeSingle() (not single()) — if RLS filters the row out (e.g. this
    // lesson's student_id doesn't match this student, or it's a group
    // lesson with no per-student RLS branch), the query legitimately
    // returns zero rows. single() turns that into a hard PostgREST 406
    // error; maybeSingle() returns null so the "Lesson not found" branch
    // below can produce one clear, actionable error message instead.
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('id', lessonId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching lesson from Supabase (student):', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      throw error;
    }

    if (!data) {
      throw new Error(`Lesson not found: ${lessonId}`);
    }

    console.log('getLessonById - loaded lesson:', data.id);
    return convertLessonFromSupabaseFormat(data);
  } catch (error) {
    console.error('Error in getLessonById:', error);
    throw error;
  }
}

/**
 * Получить список уроков для студента без контента (только метаданные для списка).
 * Полный контент загружается при входе в урок через getLessonById.
 */
export async function getLessonListForStudent(studentId) {
  try {
    let actualStudentId = studentId;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);

    if (!isUUID) {
      const { data: studentData } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', studentId)
        .maybeSingle();

      if (studentData) {
        actualStudentId = studentData.id;
      } else {
        throw new Error(`Student not found with slug: ${studentId}`);
      }
    }

    const { data, error } = await supabase
      .from('lessons')
      .select('id, title, student_id, group_id, date, time_from, time_to, status, type, created_at, updated_at')
      .eq('student_id', actualStudentId)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching lesson list for student from Supabase:', error);
      throw error;
    }

    return (data || []).map(convertLessonListFromSupabase);
  } catch (error) {
    console.error('Error in getLessonListForStudent:', error);
    throw error;
  }
}

/**
 * Получить уроки для конкретного студента
 * Работает как для преподавателей, так и для студентов
 * ВАЖНО: загружает полный контент. Для списка в профиле используйте getLessonListForStudent.
 */
export async function getLessonsForStudent(studentId) {
  try {
    console.log('getLessonsForStudent called with studentId:', studentId);
    
    // Если studentId - это UUID (из таблицы students), используем его напрямую
    // Если это slug, нужно сначала найти student.id по slug
    let actualStudentId = studentId;
    
    // Проверяем, является ли studentId UUID или slug
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
    
    if (!isUUID) {
      // Если это slug, находим student.id по invite_slug
      const { data: studentData } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', studentId)
        .maybeSingle();
      
      if (studentData) {
        actualStudentId = studentData.id;
        console.log('Found student ID by slug:', actualStudentId);
      } else {
        console.warn('Student not found by slug:', studentId);
        throw new Error(`Student not found with slug: ${studentId}`);
      }
    }
    
    // Загружаем уроки для студента (работает для студентов и преподавателей)
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('student_id', actualStudentId)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching lessons for student from Supabase:', error);
      throw error;
    }

    console.log('getLessonsForStudent - loaded lessons from Supabase:', data?.length || 0);
    if (data && data.length > 0) {
      console.log('getLessonsForStudent - Raw lessons from Supabase:', data);
      data.forEach((lesson, index) => {
        console.log(`getLessonsForStudent - Lesson ${index + 1}:`, {
          id: lesson.id,
          title: lesson.title,
          student_id: lesson.student_id,
          content: lesson.content ? Object.keys(lesson.content) : null
        });
      });
    }
    
    const converted = data.map(convertLessonFromSupabaseFormat);
    console.log('getLessonsForStudent - Converted lessons:', converted.length);
    if (converted.length > 0) {
      converted.forEach((lesson, index) => {
        console.log(`getLessonsForStudent - Converted lesson ${index + 1}:`, {
          id: lesson.id,
          title: lesson.title,
          studentId: lesson.studentId,
          containers: lesson.containers?.length || 0,
          docContainers: lesson.docContainers?.length || 0,
          bannerImage: !!lesson.bannerImage
        });
      });
    }
    
    return converted;
  } catch (error) {
    console.error('Error in getLessonsForStudent:', error);
    throw error;
  }
}

/**
 * Получить уроки для конкретной группы
 */
export async function getLessonsForGroup(groupId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated. Please log in to view lessons.');
    }

    // Получаем teacher.id по auth_user_id
    const teacherId = user.role === 'teacher' ? user.id : null;
    if (!teacherId) {
      throw new Error('User is not a teacher');
    }
    
    // Получаем teacher record
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacherId)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('teacher_id', teacher.id)
      .eq('group_id', groupId)
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching lessons for group from Supabase:', error);
      throw error;
    }

    return data.map(convertLessonFromSupabaseFormat);
  } catch (error) {
    console.error('Error in getLessonsForGroup:', error);
    throw error;
  }
}

/**
 * Обновить ТОЛЬКО статус урока в таблице lessons, не трогая content.
 *
 * getLessonListForStudent() (используется для studentLessons — список
 * в разделе Lessons) не выбирает content/containers/editorContents —
 * они всегда приходят как [] (см. convertLessonListFromSupabase). Пустой
 * массив в JS truthy, поэтому проверка
 * `lesson.containers || lesson.editorContents || lesson.whiteboards`
 * всегда true для ЛЮБОГО элемента этого списка — и вызов saveLesson()
 * со сменой статуса из Lessons реально перезаписывал content урока
 * пустым значением, стирая содержимое. Эта функция обновляет только
 * колонку status, никогда не трогая content.
 */
export async function updateLessonStatusById(id, status) {
  try {
    if (!id) return false;
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return false;

    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    if (!teacher) return false;

    const { data, error } = await supabase
      .from('lessons')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('teacher_id', teacher.id)
      .select();

    if (error) {
      console.error('updateLessonStatusById error:', error);
      return false;
    }
    return !!data && data.length > 0;
  } catch (e) {
    console.error('Error in updateLessonStatusById:', e);
    return false;
  }
}

/**
 * Сохранить или обновить урок
 */
export async function saveLesson(lessonData) {
  try {
    // Проверяем, что пользователь аутентифицирован через Supabase Auth
    const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
    if (authError || !authUser) {
      console.error('❌ saveLesson - User not authenticated via Supabase Auth:', authError);
      throw new Error('User not authenticated. Please log in to save lessons.');
    }
    
    console.log('✅ saveLesson - Auth user ID:', authUser.id);
    
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not found in database. Please log in again.');
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      throw new Error('Only teachers can save lessons');
    }
    
    console.log('✅ saveLesson - Current user:', {
      id: user.id,
      role: user.role,
      auth_user_id: user.auth_user_id || user.id
    });
    
    const { data: teacher, error: teacherError } = await supabase
      .from('teachers')
      .select('id, auth_user_id')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    
    if (teacherError || !teacher) {
      console.error('❌ saveLesson - Teacher not found:', {
        authUserId: authUser.id,
        error: teacherError
      });
      throw new Error(`Teacher not found for auth user: ${authUser.id}`);
    }
    
    console.log('✅ saveLesson - Teacher found:', {
      teacher_id: teacher.id,
      auth_user_id: teacher.auth_user_id
    });

    // Мигрируем base64 медиа (изображения, аудио, видео) в Storage перед сохранением
    const lessonIdForStorage = lessonData.id && lessonData.id !== 'new' ? lessonData.id : 'new';
    const userIdForStorage = authUser.id;
    // Migrate base64 images in editorContents (normal save flow from LessonWorkspace)
    if (Array.isArray(lessonData.editorContents)) {
      try {
        for (const editor of lessonData.editorContents) {
          if (editor.content && typeof editor.content === 'string') {
            editor.content = await migrateContentBase64(editor.content, userIdForStorage, lessonIdForStorage);
          }
        }
      } catch (migrateErr) {
        console.warn('saveLesson: editorContents migrateContentBase64 failed, saving as-is', migrateErr);
      }
    }
    // Migrate blob:/data: URLs in whiteboard shape src fields
    if (Array.isArray(lessonData.whiteboardContents)) {
      try {
        for (const wb of lessonData.whiteboardContents) {
          const shapes = wb?.content?.shapes;
          if (!Array.isArray(shapes)) continue;
          for (const shape of shapes) {
            if (shape.src && (shape.src.startsWith('blob:') || shape.src.startsWith('data:'))) {
              shape.src = await migrateMediaSrcToStorage(shape.src, userIdForStorage, lessonIdForStorage);
            }
          }
        }
      } catch (migrateErr) {
        console.warn('saveLesson: whiteboardContents migration failed, saving as-is', migrateErr);
      }
    }

    // Migrate base64 in content field (re-save of a Supabase-loaded lesson)
    if (lessonData.content) {
      try {
        lessonData.content = await migrateContentBase64(
          lessonData.content,
          userIdForStorage,
          lessonIdForStorage
        );
      } catch (migrateErr) {
        console.warn('saveLesson: migrateContentBase64 failed, saving as-is', migrateErr);
      }
    }

    // Конвертируем в Supabase формат
    const supabaseLesson = await convertLessonToSupabaseFormat(lessonData);
    supabaseLesson.teacher_id = teacher.id;

    // Обновляем updated_at
    supabaseLesson.updated_at = new Date().toISOString();

    if (lessonData.id && lessonData.id !== 'new') {
      // Проверяем, существует ли урок в Supabase
      const { data: existingLesson, error: checkError } = await supabase
        .from('lessons')
        .select('id')
        .eq('id', lessonData.id)
        .eq('teacher_id', teacher.id)
        .maybeSingle();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking lesson existence:', checkError);
        throw checkError;
      }

      if (existingLesson) {
        // Обновляем существующий урок
        console.log('Updating lesson in Supabase:', {
          id: lessonData.id,
          teacher_id: teacher.id,
          student_id: supabaseLesson.student_id,
          title: supabaseLesson.title
        });
        
        // LessonWorkspace's content save deliberately omits date/time/status
        // ("уроки из LessonWorkspace не должны попадать в расписание" — see
        // its handleSave) — but convertLessonToSupabaseFormat always fills
        // in a default (null / 'planned') for a missing field, and writing
        // those defaults unconditionally here was silently wiping out
        // whatever the Schedule/Lessons tab had already set (a lesson
        // marked 'done', then opened and saved from the editor, came back
        // as 'planned' — displayed as "In Progress" by the status
        // dropdown's own fallback). Only include a field here when the
        // caller actually provided it, so a content-only save can't touch
        // columns it has no opinion about — the same "don't touch what you
        // didn't mean to change" fix as updateLessonStatusById, just for
        // the reverse direction (content save vs. status-only update).
        const updatePayload = {
          title: supabaseLesson.title,
          student_id: supabaseLesson.student_id,
          group_id: supabaseLesson.group_id,
          content: supabaseLesson.content,
          type: supabaseLesson.type,
          updated_at: supabaseLesson.updated_at,
        };
        if (lessonData.date !== undefined) updatePayload.date = supabaseLesson.date;
        if (lessonData.timeFrom !== undefined || lessonData.from !== undefined) updatePayload.time_from = supabaseLesson.time_from;
        if (lessonData.timeTo !== undefined || lessonData.to !== undefined) updatePayload.time_to = supabaseLesson.time_to;
        if (lessonData.status !== undefined) updatePayload.status = supabaseLesson.status;

        const { data, error } = await supabase
          .from('lessons')
          .update(updatePayload)
          .eq('id', lessonData.id)
          .eq('teacher_id', teacher.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating lesson in Supabase:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log('Lesson updated successfully in Supabase:', data);
        return convertLessonFromSupabaseFormat(data);
      } else {
        // Урок не найден, создаем новый
        console.log('Lesson not found in Supabase, creating new one:', {
          original_id: lessonData.id,
          teacher_id: teacher.id,
          student_id: supabaseLesson.student_id,
          title: supabaseLesson.title
        });
        
        // Удаляем id, чтобы Supabase создал новый
        delete supabaseLesson.id;
        
        const { data, error } = await supabase
          .from('lessons')
          .insert(supabaseLesson)
          .select()
          .single();

        if (error) {
          console.error('Error creating lesson in Supabase:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }
        
        console.log('Lesson created successfully in Supabase:', data);
        return convertLessonFromSupabaseFormat(data);
      }
    } else {
      // Создаем новый урок
      console.log('Creating new lesson in Supabase:', {
        teacher_id: teacher.id,
        student_id: supabaseLesson.student_id,
        title: supabaseLesson.title,
        content_keys: Object.keys(supabaseLesson.content || {})
      });
      
      const { data, error } = await supabase
        .from('lessons')
        .insert(supabaseLesson)
        .select()
        .single();

      if (error) {
        console.error('Error creating lesson in Supabase:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log('Lesson created successfully in Supabase:', data);
      trackEvent('lesson_created', { lessonId: data.id, teacherId: teacher.id, studentId: supabaseLesson.student_id || null });
      return convertLessonFromSupabaseFormat(data);
    }
  } catch (error) {
    console.error('Error in saveLesson:', error);
    throw error;
  }
}

/**
 * Удалить урок
 */
export async function deleteLesson(lessonId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      // Fallback на localStorage
      const allLessons = getLessonsFromLocalStorage();
      const filtered = allLessons.filter(lesson => lesson.id !== lessonId);
      localStorage.setItem('novas_lessons_data', JSON.stringify(filtered));
      return true;
    }

    // Получаем teacher.id по auth_user_id
    if (user.role !== 'teacher') {
      throw new Error('Only teachers can delete lessons');
    }
    
    const { data: teacher } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    
    if (!teacher) {
      throw new Error('Teacher not found');
    }
    
    const { error } = await supabase
      .from('lessons')
      .delete()
      .eq('id', lessonId)
      .eq('teacher_id', teacher.id);

    if (error) {
      console.error('Error deleting lesson from Supabase:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteLesson:', error);
    throw error;
  }
}

